import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus, Pencil, Trash2, Home, DollarSign, Loader2, X,
  MapPin, BedDouble, Bath, Ruler, ExternalLink,
  ChevronRight, ChevronLeft, GripVertical,
  LayoutGrid, Kanban, User, Phone, Mail, Calendar, FileText,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STAGES = ['prospect', 'contacted', 'signed', 'pre_market', 'active', 'pending', 'sold'];
const STAGE_LABELS = {
  prospect: 'Prospect',
  contacted: 'Contacted',
  signed: 'Signed',
  pre_market: 'Pre-Market',
  active: 'Active',
  pending: 'Pending',
  sold: 'Sold',
};
const STAGE_COLORS = {
  prospect: { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-400', dot: 'bg-sky-400' },
  contacted: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  signed: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', dot: 'bg-violet-400' },
  pre_market: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
  active: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  pending: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', dot: 'bg-orange-400' },
  sold: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', dot: 'bg-green-400' },
};

function formatPrice(v) {
  if (!v) return '$0';
  return v >= 1000000 ? `$${(v / 1000000).toFixed(2)}M` : `$${(v / 1000).toFixed(0)}K`;
}

/* ── Listing Card for Kanban ─────────────────────────────────────── */
function ListingCard({ listing, onMove, onEdit, onDelete }) {
  return (
    <Card
      data-testid={`listing-pipeline-card-${listing.id}`}
      className="bg-card/80 border-border hover:border-border transition-all group"
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate" data-testid="listing-card-address">{listing.address}</p>
            {(listing.city || listing.state) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                <MapPin size={10} />
                <span>{[listing.city, listing.state].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
          {listing.price > 0 && (
            <span className="flex items-center gap-1">
              <DollarSign size={11} className="text-emerald-500" />
              {formatPrice(listing.price)}
            </span>
          )}
          {listing.beds > 0 && <span className="flex items-center gap-1"><BedDouble size={10} />{listing.beds}</span>}
          {listing.baths > 0 && <span className="flex items-center gap-1"><Bath size={10} />{listing.baths}</span>}
          {listing.sqft > 0 && <span className="flex items-center gap-1"><Ruler size={10} />{listing.sqft.toLocaleString()}</span>}
        </div>

        {listing.seller_name && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <User size={10} className="text-blue-400" />
            <span className="truncate">{listing.seller_name}</span>
          </div>
        )}

        {listing.estimated_list_date && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar size={10} className="text-amber-400" />
            <span>{listing.estimated_list_date}</span>
          </div>
        )}

        {/* Quick actions */}
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onMove(listing, 'prev')}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid={`move-listing-prev-${listing.id}`}
            title="Move to previous stage"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => onEdit(listing)}
            className="p-1 rounded text-muted-foreground hover:text-emerald-400 hover:bg-muted transition-colors"
            data-testid={`edit-listing-${listing.id}`}
            title="Edit"
          >
            <Pencil size={12} />
          </button>
          <GripVertical size={12} className="text-muted-foreground mx-auto" />
          <button
            onClick={() => onDelete(listing.id)}
            className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-muted transition-colors"
            data-testid={`delete-listing-${listing.id}`}
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
          <button
            onClick={() => onMove(listing, 'next')}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid={`move-listing-next-${listing.id}`}
            title="Move to next stage"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Stage Column ────────────────────────────────────────────────── */
function StageColumn({ stage, listings, color, onMove, onEdit, onDelete }) {
  return (
    <div className="flex flex-col min-w-[250px] max-w-[290px] shrink-0" data-testid={`listing-stage-column-${stage}`}>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 ${color.border} ${color.bg}`}>
        <span className={`h-2 w-2 rounded-full ${color.dot}`} />
        <h3 className={`text-sm font-semibold ${color.text}`}>{STAGE_LABELS[stage]}</h3>
        <span className="ml-auto text-xs text-muted-foreground font-mono">{listings.length}</span>
      </div>
      <ScrollArea className="flex-1 max-h-[calc(100vh-220px)]">
        <div className="p-2 space-y-2">
          {listings.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">No listings</div>
          ) : (
            listings.map(l => (
              <ListingCard key={l.id} listing={l} onMove={onMove} onEdit={onEdit} onDelete={onDelete} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ── Listing Modal (Create / Edit) with pipeline fields ──────────── */
function ListingModal({ listing, onSave, onClose }) {
  const [form, setForm] = useState({
    address: '', city: '', state: '', zip_code: '', price: 0,
    beds: 0, baths: 0, sqft: 0, property_type: 'single_family',
    description: '', listing_url: '', status: 'prospect',
    seller_name: '', seller_phone: '', seller_email: '',
    estimated_list_date: '', estimated_list_price: '',
    agreement_signed: false, agreement_date: '',
    pipeline_notes: '', source: '',
    ...(listing || {}),
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('property');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) { toast.error('Address is required'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        price: Number(form.price) || 0,
        beds: Number(form.beds) || 0,
        baths: Number(form.baths) || 0,
        sqft: Number(form.sqft) || 0,
        estimated_list_price: form.estimated_list_price ? Number(form.estimated_list_price) : null,
      });
    } catch { /* parent handles */ }
    setSaving(false);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const inputCls = 'w-full bg-card border border-border text-foreground text-sm rounded-lg px-3 py-2 focus:border-emerald-500 outline-none';
  const labelCls = 'text-xs text-muted-foreground mb-1 block';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="listing-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">{listing ? 'Edit Listing' : 'Add Listing'}</h2>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-5">
          {[{ key: 'property', label: 'Property' }, { key: 'seller', label: 'Seller / Pipeline' }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`modal-tab-${t.key}`}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                tab === t.key ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {tab === 'property' ? (
            <>
              <div>
                <label className={labelCls}>Address *</label>
                <input value={form.address} onChange={e => set('address', e.target.value)} data-testid="listing-address-input" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={labelCls}>City</label><input value={form.city} onChange={e => set('city', e.target.value)} data-testid="listing-city-input" className={inputCls} /></div>
                <div><label className={labelCls}>State</label><input value={form.state} onChange={e => set('state', e.target.value)} data-testid="listing-state-input" className={inputCls} /></div>
                <div><label className={labelCls}>Zip</label><input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div><label className={labelCls}>Price ($)</label><input type="number" value={form.price} onChange={e => set('price', e.target.value)} data-testid="listing-price-input" className={inputCls} /></div>
                <div><label className={labelCls}>Beds</label><input type="number" value={form.beds} onChange={e => set('beds', e.target.value)} data-testid="listing-beds-input" className={inputCls} /></div>
                <div><label className={labelCls}>Baths</label><input type="number" step="0.5" value={form.baths} onChange={e => set('baths', e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Sqft</label><input type="number" value={form.sqft} onChange={e => set('sqft', e.target.value)} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select value={form.property_type} onChange={e => set('property_type', e.target.value)} className={inputCls}>
                    <option value="single_family">Single Family</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="multi_family">Multi Family</option>
                    <option value="land">Land</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Stage</label>
                  <select value={form.status} onChange={e => set('status', e.target.value)} data-testid="listing-status-select" className={inputCls}>
                    {PIPELINE_STAGES.map(s => (
                      <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={2}
                  className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls}>Listing URL</label>
                <input value={form.listing_url || ''} onChange={e => set('listing_url', e.target.value)} className={inputCls} placeholder="https://..." />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Seller Name</label>
                  <input value={form.seller_name || ''} onChange={e => set('seller_name', e.target.value)} data-testid="listing-seller-name" className={inputCls} placeholder="John Doe" />
                </div>
                <div>
                  <label className={labelCls}>Seller Phone</label>
                  <input value={form.seller_phone || ''} onChange={e => set('seller_phone', e.target.value)} data-testid="listing-seller-phone" className={inputCls} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Seller Email</label>
                <input value={form.seller_email || ''} onChange={e => set('seller_email', e.target.value)} data-testid="listing-seller-email" className={inputCls} placeholder="seller@email.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Est. List Date</label>
                  <input type="date" value={form.estimated_list_date || ''} onChange={e => set('estimated_list_date', e.target.value)} data-testid="listing-est-date" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Est. List Price ($)</label>
                  <input type="number" value={form.estimated_list_price || ''} onChange={e => set('estimated_list_price', e.target.value)} data-testid="listing-est-price" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Agreement Signed</label>
                  <select value={form.agreement_signed ? 'yes' : 'no'} onChange={e => set('agreement_signed', e.target.value === 'yes')} className={inputCls}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Agreement Date</label>
                  <input type="date" value={form.agreement_date || ''} onChange={e => set('agreement_date', e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Source</label>
                <input value={form.source || ''} onChange={e => set('source', e.target.value)} data-testid="listing-source" className={inputCls} placeholder="Referral, Cold Call, Door Knock..." />
              </div>
              <div>
                <label className={labelCls}>Pipeline Notes</label>
                <textarea value={form.pipeline_notes || ''} onChange={e => set('pipeline_notes', e.target.value)} rows={3}
                  className={`${inputCls} resize-none`} data-testid="listing-pipeline-notes" placeholder="Notes about this listing opportunity..." />
              </div>
            </>
          )}

          <button type="submit" disabled={saving} data-testid="listing-save-btn"
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : (listing ? 'Update Listing' : 'Add Listing')}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Grid Card (for table/grid view) ─────────────────────────────── */
function GridCard({ listing, onEdit, onDelete }) {
  const color = STAGE_COLORS[listing.status] || STAGE_COLORS.prospect;
  return (
    <Card className="bg-card/80 border-border hover:border-border transition-colors" data-testid={`listing-card-${listing.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate" data-testid="listing-card-address">{listing.address}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <MapPin size={11} />
              <span>{[listing.city, listing.state].filter(Boolean).join(', ')}</span>
            </div>
          </div>
          <Badge className={`text-[10px] shrink-0 ${color.bg} ${color.text}`}>{STAGE_LABELS[listing.status] || listing.status}</Badge>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><DollarSign size={11} className="text-emerald-500" />{formatPrice(listing.price)}</span>
          {listing.beds > 0 && <span className="flex items-center gap-1"><BedDouble size={11} />{listing.beds} bd</span>}
          {listing.baths > 0 && <span className="flex items-center gap-1"><Bath size={11} />{listing.baths} ba</span>}
          {listing.sqft > 0 && <span className="flex items-center gap-1"><Ruler size={11} />{listing.sqft.toLocaleString()} sqft</span>}
        </div>
        {listing.seller_name && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <User size={11} className="text-blue-400" /><span>{listing.seller_name}</span>
          </div>
        )}
        {listing.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{listing.description}</p>}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
          <button onClick={() => onEdit(listing)} data-testid={`grid-edit-listing-${listing.id}`}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Pencil size={12} /> Edit
          </button>
          {listing.listing_url && (
            <a href={listing.listing_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors ml-auto">
              <ExternalLink size={12} /> View
            </a>
          )}
          <button onClick={() => onDelete(listing.id)} data-testid={`grid-delete-listing-${listing.id}`}
            className="flex items-center gap-1 text-xs text-red-400/60 hover:text-red-400 transition-colors ml-auto">
            <Trash2 size={12} />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Main Page ───────────────────────────────────────────────────── */
export default function ListingsPage() {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('pipeline');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchListings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/listings`, { headers });
      setListings(data);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const saveListing = async (form) => {
    try {
      if (editing) {
        await axios.put(`${API}/listings/${editing.id}`, form, { headers });
        toast.success('Listing updated');
      } else {
        await axios.post(`${API}/listings`, form, { headers });
        toast.success('Listing added');
      }
      setShowModal(false);
      setEditing(null);
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/listings/${id}`, { headers });
      toast.success('Listing deleted');
      fetchListings();
    } catch { toast.error('Failed to delete'); }
  };

  const moveListing = async (listing, direction) => {
    const currentIdx = PIPELINE_STAGES.indexOf(listing.status || 'prospect');
    const newIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
    if (newIdx < 0 || newIdx >= PIPELINE_STAGES.length) return;
    const newStage = PIPELINE_STAGES[newIdx];

    // Optimistic update
    setListings(prev => prev.map(l => l.id === listing.id ? { ...l, status: newStage } : l));

    try {
      await axios.patch(`${API}/listings/${listing.id}/stage`, { stage: newStage }, { headers });
    } catch {
      toast.error('Failed to move listing');
      fetchListings();
    }
  };

  const openEdit = (listing) => {
    setEditing(listing);
    setShowModal(true);
  };

  // Group listings by stage for pipeline view
  const board = {};
  PIPELINE_STAGES.forEach(s => { board[s] = []; });
  listings.forEach(l => {
    const stage = PIPELINE_STAGES.includes(l.status) ? l.status : 'prospect';
    board[stage].push(l);
  });

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>;

  return (
    <div className="h-full flex flex-col" data-testid="listings-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border">
        <div>
          <h1 className="text-lg font-semibold text-foreground" data-testid="listings-title">Listings Pipeline</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{listings.length} properties across {PIPELINE_STAGES.length} stages</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-card border border-border rounded-lg p-0.5" data-testid="view-toggle">
            <button onClick={() => setView('pipeline')} data-testid="view-pipeline-btn"
              className={`p-1.5 rounded transition-colors ${view === 'pipeline' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <Kanban size={16} />
            </button>
            <button onClick={() => setView('grid')} data-testid="view-grid-btn"
              className={`p-1.5 rounded transition-colors ${view === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <LayoutGrid size={16} />
            </button>
          </div>
          <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="add-listing-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors">
            <Plus size={16} /> Add Listing
          </button>
        </div>
      </div>

      {/* Content */}
      {listings.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="listings-empty">
          <Home size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-base text-muted-foreground">No listings yet</p>
          <p className="text-sm mt-1 text-muted-foreground">Add your first property to start tracking your pipeline</p>
        </div>
      ) : view === 'pipeline' ? (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-3 p-4 min-h-full" data-testid="listings-pipeline-board">
            {PIPELINE_STAGES.map(stage => (
              <StageColumn
                key={stage}
                stage={stage}
                listings={board[stage]}
                color={STAGE_COLORS[stage]}
                onMove={moveListing}
                onEdit={openEdit}
                onDelete={deleteListing}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="listings-grid">
            {listings.map(l => (
              <GridCard key={l.id} listing={l} onEdit={openEdit} onDelete={deleteListing} />
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <ListingModal listing={editing} onSave={saveListing} onClose={() => { setShowModal(false); setEditing(null); }} />
      )}
    </div>
  );
}
